import { mcpRequestKey } from "./mcp-consent";
/**
 * Engine public API helper.
 * Used by the slug-based public chat page (engine-chat.tsx).
 *
 * Two distinct token classes live in localStorage under distinct keys
 * so they never collide:
 *
 *   engine_user_token_<slug>
 *     The user's real login JWT (mode=credentials/registration/open/sso).
 *     24-hour TTL (issueEngineToken, internal/handler/engine.go); a scribe
 *     recording re-issues it at /scribe/start so a meeting always begins
 *     with a full day of runway. Carries accountId. Survives tab close +
 *     browser restart.
 *
 *   engine_guest_token_<slug>_<meetingId>
 *     A meeting-scoped guest JWT (mode=guest, 4-hour TTL) minted by
 *     /api/engine/auth/meeting-guest. Carries meetingId + participantId,
 *     NOT accountId. Lives only for the duration of one meeting. Cleared
 *     on Leave/Denied/Ended.
 *
 * BEFORE: GuestJoinForm wrote the guest token to engine_user_token_<slug>,
 * clobbering the user's real session. A logged-in user who happened to
 * guest-join a meeting lost their account on that slug — admit calls
 * 403'd, MeetingsView came up empty, Settings showed "—" for email.
 * (Reported via meeting b59a1bea where the host couldn't admit attendees.)
 *
 * NOW: enginePublicFetch detects /meetings/<id>/... paths and prefers
 * the guest token for that meetingId if one exists, falling back to the
 * user token otherwise. Non-meeting paths always use the user token.
 * Result: a guest in meeting A can still be logged in as themselves
 * elsewhere on the slug.
 *
 * Earlier this used sessionStorage, which threw away the session every
 * tab close.
 */

import { engineHostSlug, engineBasePath } from "@/lib/app-routing";
import { tzHeaders } from "@/lib/timezone";

/** localStorage key of the slug's user token. Exported so a recording tab can
 * watch for a sign-in landing from another tab (the `storage` event). */
export function userTokenKey(slug: string) {
  return `engine_user_token_${slug}`;
}

function guestTokenKey(slug: string, meetingId: string) {
  return `engine_guest_token_${slug}_${meetingId}`;
}

/**
 * Build the in-app path prefix that addresses tenant `slug` on the current host.
 *   per-tenant subdomain (slug === host slug) → "" (tenant is the origin; callers
 *     append "/v/<view>" etc., and use `... || "/"` for the bare tenant root)
 *   engine.* app subdomain                    → "/<slug>"
 *   apex / localhost                          → "/engine/<slug>"
 * Single source of truth lives in app-routing.ts so this can't drift from the
 * route matcher in App.tsx again.
 */
export function engineSlugPath(slug: string): string {
  if (engineHostSlug() === slug) return "";
  return `${engineBasePath()}/${slug}`;
}

// In-memory token providers by slug — registered by surfaces whose JWT must
// never touch persistent storage (the third-party embed widget holds its
// short-lived HMAC-handoff token in memory only). A registered provider
// takes precedence over localStorage for every consumer of
// getEngineUserToken — enginePublicFetch/Upload, the chat session hook's
// SSE auth, AuthImage, claim decoding — so the whole first-party chat stack
// works unmodified on an in-memory token.
const engineTokenProviders = new Map<string, () => string | null>();

// A provider may come with a renewer, called when a request is refused as
// unauthorized: it resolves true once the provider holds a fresh token, and
// enginePublicFetch then repeats the request once. The embed widget's token
// lasts minutes and can only be re-minted through its host page, so without
// this a missed refresh leaves every call failing until a reload.
const engineTokenRenewers = new Map<string, () => Promise<boolean>>();

export function setEngineTokenProvider(
  slug: string,
  provider: (() => string | null) | null,
  renew?: () => Promise<boolean>,
) {
  if (provider) engineTokenProviders.set(slug, provider);
  else engineTokenProviders.delete(slug);
  if (provider && renew) engineTokenRenewers.set(slug, renew);
  else engineTokenRenewers.delete(slug);
}

export function getEngineUserToken(slug: string): string | null {
  const provider = engineTokenProviders.get(slug);
  if (provider) return provider();
  return localStorage.getItem(userTokenKey(slug));
}

export function setEngineUserToken(slug: string, token: string) {
  localStorage.setItem(userTokenKey(slug), token);
}

export function clearEngineUserToken(slug: string) {
  localStorage.removeItem(userTokenKey(slug));
}

// === Microsoft Entra ID (Azure AD) SSO ===

/** Kick off interactive Microsoft sign-in for a tenant. A full-page navigate to
 * the backend, which redirects to Microsoft and (after auth) back to the SPA at
 * "/#entra_sso=<slug>|<jwt>". */
export function startEntraLogin(slug: string) {
  teamsConnectChallenge(slug);
  // Carry the originating URL so the callback returns the browser to THIS origin
  // (a per-tenant subdomain like <slug>.<env>.shopai.website, or engine.*), not the
  // fixed callback origin. The user token (browser storage, keyed by slug) is
  // origin-bound, so it must land where the user began. The backend allow-lists the
  // host (open-redirect guard) — same mechanism as startEntraAdminLogin.
  const ret = window.location.origin + window.location.pathname;
  window.location.href = `/api/engine/auth/entra/login?slug=${encodeURIComponent(slug)}&return=${encodeURIComponent(ret)}`;
}

/** Kick off interactive Microsoft sign-in for the ShopAI engine-admin console.
 * Lands back at "/#entra_admin=<adminJwt>", consumed at boot below. */
export function startEntraAdminLogin(returnUrl?: string) {
  // The FULL current URL (origin + path) so the callback can redirect the browser
  // back to that exact ORIGIN with the admin token in the fragment. Apps live on
  // different subdomains (smartmail.<env> vs engine.<env>) and the token
  // (localStorage) is origin-bound, so it must be handed to the right origin. The
  // backend allow-lists the host (open-redirect guard).
  const ret = returnUrl ?? window.location.origin + window.location.pathname;
  window.location.href = `/api/engine/auth/entra/admin-login?return=${encodeURIComponent(ret)}`;
}

/** Consume the "#entra_sso=<slug>|<jwt>" (or "#entra_error=<code>") fragment the
 * Entra callback redirects to. On success: store the user token under the slug,
 * strip the fragment, and navigate to that tenant — engine-chat then picks the
 * token up exactly like a password login. Call once at app boot, before the
 * engine reads the token. No-op when the fragment is absent. */
export function consumeEntraSSO() {
  const hash = window.location.hash;
  if (hash.startsWith("#entra_admin=")) {
    // The backend already redirected us to the correct origin/URL (the validated
    // return), with the admin token in this fragment. Store it on THIS origin and
    // let the app render authenticated — consumeEntraSSO runs before React mounts,
    // so no reload/navigation is needed.
    const token = decodeURIComponent(hash.slice("#entra_admin=".length));
    history.replaceState(null, "", window.location.pathname + window.location.search);
    // Peer sign-in for the dev→prod Sync panel: this token was minted by the
    // PEER environment and must NOT become this origin's admin session — it is
    // signed with the peer's SESSION_SECRET, so storing it here would log the
    // operator out of the console they are standing in. SyncPanel sets the
    // pending flag immediately before redirecting; sessionStorage survives the
    // SSO round trip within this tab and nowhere else. The flag (not the return
    // path) is the discriminator because the callback's return-URL allow-list
    // strips query strings, leaving no room for a marker there.
    if (sessionStorage.getItem("sync_peer_login_pending")) {
      sessionStorage.removeItem("sync_peer_login_pending");
      if (token) sessionStorage.setItem("sync_peer_token", token);
      return;
    }
    if (token) localStorage.setItem("engine_token", token); // ShopAI engine-admin console token
    return;
  }
  if (hash.startsWith("#entra_sso=")) {
    try {
      const raw = decodeURIComponent(hash.slice("#entra_sso=".length));
      const sep = raw.indexOf("|");
      if (sep > 0) {
        const slug = raw.slice(0, sep);
        const token = raw.slice(sep + 1);
        if (slug && token) {
          setEngineUserToken(slug, token);
          history.replaceState(null, "", window.location.pathname + window.location.search);
          window.location.replace(teamsConnectChallenge(slug)
            ? `${engineSlugPath(slug)}/v/teams-connect`
            : sessionStorage.getItem(mcpRequestKey(slug))
            ? `${engineSlugPath(slug)}/v/connections`
            : engineSlugPath(slug) || "/");
          return;
        }
      }
    } catch {
      /* malformed fragment — fall through and clear it */
    }
    history.replaceState(null, "", window.location.pathname + window.location.search);
  } else if (hash.startsWith("#entra_error=")) {
    const code = decodeURIComponent(hash.slice("#entra_error=".length));
    history.replaceState(null, "", window.location.pathname + window.location.search);
    // eslint-disable-next-line no-alert
    alert(`Microsoft sign-in failed: ${code}`);
  }
}

// === Meeting-scoped guest tokens ===

/** Read the guest token for a specific meeting. Used by the request-pipe
 * routing in enginePublicFetch and by GuestJoinForm's lobby polling. */
export function getEngineGuestToken(slug: string, meetingId: string): string | null {
  return localStorage.getItem(guestTokenKey(slug, meetingId));
}

/** Store the guest token returned by /api/engine/auth/meeting-guest. */
export function setEngineGuestToken(slug: string, meetingId: string, token: string) {
  localStorage.setItem(guestTokenKey(slug, meetingId), token);
}

/** Clear the guest token. Called on Leave/Denied/Ended cleanup paths. */
export function clearEngineGuestToken(slug: string, meetingId: string) {
  localStorage.removeItem(guestTokenKey(slug, meetingId));
}

/** One-time cleanup of legacy token-storage state left over from the
 * pre-distinct-key world. Two artefacts to scrub:
 *
 *   1. engine_user_token_<slug> currently holding a guest-mode JWT —
 *      the old GuestJoinForm wrote guest tokens to the user-token key,
 *      shadowing real account sessions. We can't tell from inside the
 *      app whether the stored JWT is a leftover guest token from the
 *      old code path or a fresh user token; the only safe heuristic is
 *      to decode and check mode==="guest", in which case the key holds
 *      something the new layout would never put there → clear it.
 *
 *   2. engine_user_token_<slug>__prev (the __prev backup from commit
 *      c49012ac, a band-aid over the same key collision). With the new
 *      distinct guest-token key, the __prev backup is dead weight and
 *      we just delete it.
 *
 * Idempotent. Safe to call on every page load. */
export function cleanupLegacyEngineTokens(slug: string) {
  const userKey = userTokenKey(slug);
  const backupKey = `${userKey}__prev`;
  try {
    const v = localStorage.getItem(userKey);
    if (v) {
      const payload = v.split(".")[1];
      if (payload) {
        try {
          const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
          if (json && json.mode === "guest") {
            localStorage.removeItem(userKey);
          }
        } catch { /* malformed JWT — leave it; /account/me will reject */ }
      }
    }
    localStorage.removeItem(backupKey);
  } catch {
    // localStorage blocked / disabled — nothing we can do, app will
    // fall through to a fresh login anyway.
  }
}

// extractMeetingIdFromPath pulls the meeting UUID out of paths like
//   /meetings/<id>/join
//   /meetings/<id>/files/<fileId>/download
//   /meetings/<id>/outcome/generate
// Returns "" when the path isn't meeting-scoped. UUID match is loose on
// purpose — anything between two slashes after /meetings/ is treated as
// the id; the server validates the UUID shape and rejects garbage.
function extractMeetingIdFromPath(path: string): string {
  const m = /^\/meetings\/([^/]+)(\/|$)/.exec(path);
  return m ? m[1] : "";
}

// pickAuthToken returns the right Bearer token for a given /engine/public
// path: prefer a meeting-scoped guest token when one exists for the path's
// meetingId, otherwise fall back to the slug's user token. This is what
// keeps a logged-in user logged in even while they're a guest in someone
// else's meeting in another tab on the same slug.
function pickAuthToken(slug: string, path: string): string | null {
  const meetingId = extractMeetingIdFromPath(path);
  if (meetingId) {
    const guestTok = getEngineGuestToken(slug, meetingId);
    if (guestTok) return guestTok;
  }
  return getEngineUserToken(slug);
}

/** Decode the engine user JWT payload to read claim fields client-side
 * (e.g. accountId to test whether the current user organizes a meeting).
 * Returns null when no token is stored or the token is malformed. */
export function getEngineUserClaims(slug: string): { accountId?: string; tenantId?: string; mode?: string; isAdmin?: boolean } | null {
  const token = getEngineUserToken(slug);
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    // Base64URL decode; replace URL-safe chars first.
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(atob(payload + "=".repeat((4 - payload.length % 4) % 4)));
    return decoded;
  } catch {
    return null;
  }
}

/** Authenticated fetch for engine public endpoints.
 *
 * For paths matching /meetings/<id>/..., picks a meeting-scoped guest
 * token (if one exists for that meetingId) over the user token. This is
 * how a guest's meeting actions route through their guest JWT while the
 * same user's account session on other tabs stays untouched. */
export async function enginePublicFetch(slug: string, path: string, opts?: RequestInit): Promise<Response> {
  const run = () => {
    const token = pickAuthToken(slug, path);
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...tzHeaders(),
      ...(opts?.headers as Record<string, string>),
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return fetch(`/api/engine/public${path}`, { ...opts, headers });
  };
  const res = await run();
  // One repeat, and only where a renewer is registered (see
  // setEngineTokenProvider): the token is re-read, so the repeat carries the
  // renewed one. Only the token check's own refusal counts (it alone sets
  // this header) — a handler's 401 is an answer about the request, and a new
  // token would not change it.
  const tokenRefused = res.status === 401 && (res.headers.get("WWW-Authenticate") || "").includes("invalid_token");
  const renew = tokenRefused ? engineTokenRenewers.get(slug) : undefined;
  if (!renew || !(await renew())) return res;
  return run();
}

/** Unauthenticated fetch for engine auth endpoints. */
export async function engineAuthFetch(path: string, opts?: RequestInit): Promise<Response> {
  return fetch(`/api/engine/auth${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts?.headers as Record<string, string>),
    },
  });
}

/** Authenticated multipart upload for engine public endpoints.
 * Does NOT set Content-Type — browser sets multipart boundary automatically.
 * Same guest-token-aware path routing as enginePublicFetch.
 *
 * `signal` lets the caller abort the upload mid-flight — used by the chat
 * voice flow so a follow-up VAD utterance can cut the previous one off
 * instead of being silently dropped while the previous SSE response is
 * still streaming. */
export async function enginePublicUpload(slug: string, path: string, formData: FormData, signal?: AbortSignal): Promise<Response> {
  const token = pickAuthToken(slug, path);
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return fetch(`/api/engine/public${path}`, { method: "POST", headers, body: formData, signal });
}

/** Fetch tenant info by slug (public, no auth needed). */
export async function fetchTenantBySlug(slug: string) {
  const res = await fetch(`/api/engine/tenants/by-slug/${slug}`);
  if (!res.ok) throw new Error("Tenant not found");
  return res.json();
}

// === Password Setup ===

export async function requestPasswordSetup(tenantId: string, email: string) {
  const res = await engineAuthFetch("/password-setup/request", {
    method: "POST",
    body: JSON.stringify({ tenantId, email }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(body.message || res.statusText);
  }
  return res.json();
}

export async function confirmPasswordSetup(tenantId: string, email: string, code: string, password: string) {
  const res = await engineAuthFetch("/password-setup/confirm", {
    method: "POST",
    body: JSON.stringify({ tenantId, email, code, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(body.message || res.statusText);
  }
  return res.json();
}

// === TOTP ===

export async function validateTOTP(pendingToken: string, totpCode: string) {
  const res = await engineAuthFetch("/totp/validate", {
    method: "POST",
    body: JSON.stringify({ pendingToken, totpCode }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(body.message || res.statusText);
  }
  return res.json();
}

export async function setupUserTOTP(slug: string) {
  const res = await enginePublicFetch(slug, "/totp/setup", { method: "POST" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(body.message || res.statusText);
  }
  return res.json();
}

export async function verifyUserTOTP(slug: string, code: string) {
  const res = await enginePublicFetch(slug, "/totp/verify", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(body.message || res.statusText);
  }
  return res.json();
}

export async function skipUserTOTP(slug: string) {
  const res = await enginePublicFetch(slug, "/totp/skip", { method: "POST" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(body.message || res.statusText);
  }
  return res.json();
}

// The popup challenge is public, scoped to this tab/tenant and short lived.
// Never store the private proof or a Teams control credential here.
export function teamsConnectChallenge(slug: string): string {
  if (!window.location.pathname.endsWith("/v/teams-connect")) return "";
  const key = `teams-connect:${slug}`;
  const challenge = new URLSearchParams(window.location.search).get("challenge");
  if (challenge && /^[A-Za-z0-9_-]{43}$/.test(challenge)) {
    const chatId = new URLSearchParams(window.location.search).get("chatId") || "";
    sessionStorage.setItem(key, JSON.stringify({challenge, chatId: chatId.length <= 512 ? chatId : "", expires: Date.now()+600000}));
    return challenge;
  }
  try { const saved = JSON.parse(sessionStorage.getItem(key) || "null");
    if (saved?.expires > Date.now() && /^[A-Za-z0-9_-]{43}$/.test(saved.challenge)) return saved.challenge;
  } catch {}
  sessionStorage.removeItem(key); return "";
}
export function teamsConnectChatID(slug: string): string {
  if (!teamsConnectChallenge(slug)) return "";
  try { return JSON.parse(sessionStorage.getItem(`teams-connect:${slug}`) || "null")?.chatId || ""; } catch { return ""; }
}
export function clearTeamsConnectChallenge(slug: string) { sessionStorage.removeItem(`teams-connect:${slug}`); }
